/**
 * ELF-CIRCLE — Monthly Networking Events (design artboard `#7d`).
 *
 * A distinct owned property from Made In New Jersey — recurring, not a smaller
 * festival. The homepage's lanyard pass heads the page, carrying the next
 * date; then the next-up card with the run count beside it, and the
 * proven-cadence tiles.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfHeroNav from '../ElfHeroNav';
import { ElfPassHero } from '../ElfHeroPass';
import { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import stageImage from '../../../../../assets/elf/events-stage.jpg';

/** Next event, in iCalendar's UTC basic format. */
const NEXT_EVENT = {
  uid: 'elf-networking-2026-09-24@elfcircle.com',
  start: '20260924T230000Z',
  end: '20260925T020000Z',
  summary: 'ELF-CIRCLE Networking — Newark, NJ',
  location: 'The Yard, Newark, NJ',
  description: 'Creatives across music, film and fashion, plus sponsors and investors.',
};

/** The next date as the page words it — on the pass and the next-up card. */
const NEXT_UP = { date: 'Sep 24', city: 'Newark, NJ', venue: 'The Yard' };
const EVENTS_HOSTED = 14;

const CADENCE = [
  { when: 'Aug 27', where: 'Jersey City · 90 RSVPs' },
  { when: 'Jul 23', where: 'Hoboken · 74 RSVPs' },
  { when: 'Jun 25', where: 'Newark · 61 RSVPs' },
  { when: 'May 28', where: 'Jersey City · 55 RSVPs' },
];

/** Minimal iCalendar file for the next event, built client-side. */
function downloadIcs() {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ELF-CIRCLE//Networking//EN',
    'BEGIN:VEVENT',
    `UID:${NEXT_EVENT.uid}`,
    `DTSTART:${NEXT_EVENT.start}`,
    `DTEND:${NEXT_EVENT.end}`,
    `SUMMARY:${NEXT_EVENT.summary}`,
    `LOCATION:${NEXT_EVENT.location}`,
    `DESCRIPTION:${NEXT_EVENT.description}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'elf-circle-networking.ics';
  a.click();
  URL.revokeObjectURL(url);
}

export const NetworkingEvents: React.FC = () => {
  const consult = useConsult();
  const rsvp = (source: string, title: string) => () =>
    consult?.open({ source, interest: 'Events & Festivals', title });
  return (
  <ElfShell>
    {/* The homepage's pass, hung with this page's own date: the next event on
        the front, the proven cadence on the back. */}
    <ElfPassHero
      nav={<ElfHeroNav current="/networking-events" />}
      backdrop={stageImage}
      above={<ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Events']} />}
      eyebrow="Monthly · recurring"
      title={[
        'The room fills',
        <>
          every <em>month.</em>
        </>,
      ]}
      lede="A recurring room for creatives, sponsors, and investors — every month, market by market."
      actions={
        <>
          <button type="button" className="cta mag" onClick={rsvp('events.hero', 'RSVP — next event')}>
            RSVP next event
          </button>
          <button type="button" className="discover" onClick={downloadIcs}>
            Add to calendar <i>↓</i>
          </button>
        </>
      }
      straps={['ELF-CIRCLE · ELF-CIRCLE · ELF-CIRCLE ·', 'MONTHLY · RECURRING · MONTHLY · RECURRING ·']}
      front={{
        headline: NEXT_UP.date,
        holder: (
          <>
            {NEXT_UP.city}
            <br />
            {NEXT_UP.venue}
          </>
        ),
        metaLabel: 'Hosted',
        meta: `${EVENTS_HOSTED} events · since 2025`,
      }}
      back={{ head: 'Proven cadence', rows: CADENCE.map((c) => [c.when, c.where]) }}
      flipReveals="the last four events"
    />
    <div className="elf-page">
      <div className="elf-page__panel">
        <div
          className="elf-page__split"
          style={{ padding: '34px var(--pgut) 8px', display: 'grid', gridTemplateColumns: '1.3fr .7fr', gap: 20 }}
        >
          <div className="nextup">
            <span className="nextup__when">Next up — {NEXT_UP.date}</span>
            <span className="nextup__where">
              {NEXT_UP.city} · {NEXT_UP.venue}
            </span>
            <span className="wbody" style={{ fontSize: 14 }}>
              Creatives across music, film, and fashion — plus the sponsors and investors who fund
              the next one.
            </span>
            <div className="nextup__actions" style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button
                className="chipx"
                type="button"
                style={{ borderRadius: 99, padding: '8px 16px' }}
                onClick={rsvp('events.nextup', 'RSVP — Newark, Sep 24')}
              >
                RSVP
              </button>
              {/* Downloads a real .ics rather than opening a form — the point
                  is to get the date into their calendar, not capture a lead. */}
              <button
                className="chipx"
                type="button"
                style={{ borderRadius: 99, padding: '8px 16px' }}
                onClick={downloadIcs}
              >
                Add to calendar
              </button>
            </div>
          </div>
          <div className="runcount">
            <span className="runcount__n">{EVENTS_HOSTED}</span>
            <span className="runcount__l">events hosted, running monthly since 2025</span>
          </div>
        </div>

        <div style={{ padding: '26px var(--pgut) 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span className="colhead" style={{ padding: '0 0 6px' }}>
            Proven cadence
          </span>
          <div
            className="elf-page__grid4"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}
          >
            {CADENCE.map((c) => (
              <div className="catrow catrow--tile" key={c.when}>
                <span className="whead" style={{ fontSize: 15 }}>
                  {c.when}
                </span>
                <span className="wtag" style={{ fontSize: 12 }}>
                  {c.where}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div
          className="elf-page__grid2"
          style={{
            padding: '32px var(--pgut) 52px',
            borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            marginTop: 26,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
          }}
        >
          <Link className="nextlink" to={route('/portfolio')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="statl">Different room</span>
              <span className="whead" style={{ fontSize: 20 }}>
                Made In New Jersey
              </span>
              <span className="wtag" style={{ fontSize: 12.5 }}>
                The annual, statewide festival.
              </span>
            </div>
            <span className="arw">→</span>
          </Link>
          <button
            type="button"
            className="nextlink"
            onClick={rsvp('events.sponsor', 'Become a sponsor')}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="statl">Lower commitment</span>
              <span className="whead" style={{ fontSize: 20 }}>
                Become a sponsor
              </span>
              <span className="wtag" style={{ fontSize: 12.5 }}>
                Get in the room first.
              </span>
            </div>
            <span className="arw">→</span>
          </button>
        </div>
      </div>
    </div>
    <ElfFooter />
  </ElfShell>
  );
};

export default NetworkingEvents;
